import type { ReactNode } from 'react';
import type { Control, Path, RegisterOptions } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { FormInput } from '@ui/uikit/components/common/FormInput';
import { Minus } from '@ui/uikit/components/icons/mono/Minus';
import { Plus } from '@ui/uikit/components/icons/mono/Plus';
import { formatReward } from '@ui/uikit/utils';

import { AmountPresetButtons } from './AmountPresetButtons';

type PresetsConfig = {
  show?: boolean;
  variant?: 'number' | 'percents';
  values?: number[];
};

const DEFAULT_PRESETS_VALUES = [5, 10, 25];

type Props<T extends { amount: string }> = {
  name: Path<T>;
  label?: string;
  // @todo Refactoring for accepting any key instead of amount
  control: Control<T>;
  min: number;
  max: number;
  decimals?: number;
  presets?: PresetsConfig;
  showPlusMinus?: boolean;
  children?: ReactNode;
  renderHintMessage?: (errorMessage?: string) => ReactNode;
  onChangeValue?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  rules?: RegisterOptions<T, Path<T>>;
  rightLabel?: string;
};

const formatNumberInput = (
  value: string,
  minLimit: number,
  maxLimit: number,
  decimals: number,
): string => {
  if (value === '') {
    return value;
  }

  let sanitizedValue = value.replace(/[^\d.]/g, '');

  const dotCount = sanitizedValue.split('.').length - 1;

  if (dotCount > 1) {
    sanitizedValue = sanitizedValue.slice(0, sanitizedValue.lastIndexOf('.'));
  }

  if (decimals === 0) {
    sanitizedValue = sanitizedValue.split('.')[0];
  } else {
    const decimalIndex = sanitizedValue.indexOf('.');

    if (decimalIndex !== -1) {
      sanitizedValue = sanitizedValue.slice(0, decimalIndex + decimals + 1);
    }
  }

  const numberValue = Number(sanitizedValue);

  if (numberValue < minLimit) {
    return String(
      formatReward(minLimit, {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
        useGrouping: false,
      }),
    );
  }

  if (numberValue > maxLimit) {
    return String(
      formatReward(Math.max(minLimit, maxLimit), {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
        useGrouping: false,
      }),
    );
  }

  return sanitizedValue;
};

const defaultPresets = { show: true, variant: 'number', values: DEFAULT_PRESETS_VALUES };

export const FormAmountInputWithPresets = <T extends { amount: string }>({
  label = '',
  name,
  control,
  min,
  max,
  decimals = 0,
  children,
  presets,
  showPlusMinus = true,
  renderHintMessage = (error?: string) => error ?? '',
  onChangeValue,
  placeholder = '',
  rightLabel = '',
  rules,
}: Props<T>) => {
  const resultPresets = {
    ...defaultPresets,
    ...(presets ?? {}),
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { ref, onChange, value, ...field }, fieldState }) => {
        const handleChange = (v: string) => {
          const formatedValue = formatNumberInput(String(v), min, max, decimals);
          onChange(formatedValue);

          if (onChangeValue) {
            onChangeValue(formatedValue);
          }
        };

        const handleIncrement = (increment: number) => () => {
          const newValue = String(Number(value) + increment);
          handleChange(newValue);
        };

        const handlePresetButtonClick = (v: string) => {
          const resultValue = resultPresets?.variant === 'number' ? v : (+v * +max) / 100;

          handleChange(String(+v === +max ? max : resultValue));
        };

        return (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <FormInput
                inputRef={ref}
                onValueChange={handleChange}
                value={value}
                label={label}
                required={Boolean(label)}
                data-qa="profile-display-name-input"
                isError={!!fieldState.error}
                hint={renderHintMessage(fieldState.error?.message)}
                placeholder={placeholder}
                rightLabel={rightLabel}
                afterInput={
                  showPlusMinus && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        data-skeleton-target="base"
                        value={value}
                        className="msq-btn msq-btn-secondary msq-btn-icon-lg"
                        onClick={handleIncrement(-1)}
                      >
                        <Minus className="size-4" />
                      </button>
                      <button
                        type="button"
                        data-skeleton-target="base"
                        value={value}
                        className="msq-btn msq-btn-secondary msq-btn-icon-lg"
                        onClick={handleIncrement(1)}
                      >
                        <Plus className="size-4" />
                      </button>
                    </div>
                  )
                }
                {...field}
              />
              {resultPresets?.show && (
                <AmountPresetButtons
                  onChange={handlePresetButtonClick}
                  max={max}
                  presetValues={resultPresets.values.map((v) => {
                    return {
                      value: String(v),
                      title: resultPresets.variant === 'percents' ? `${v}%` : `${v}`,
                    };
                  })}
                />
              )}
            </div>
            {children}
          </div>
        );
      }}
      rules={rules}
    />
  );
};
